import { useCallback, useState } from "react";
import { ANNIE_NAME_MAX_LENGTH, isValidAnnieName, toSavedAnnieName } from "./annieName";

/** `undefined` = settings not loaded yet; `null` = loaded, never renamed. */
type SavedName = string | null | undefined;

interface NameDraftState {
	value: string;
	seededFromSaved: boolean;
}

// DEFAULT: never renamed starts the field empty, which saves as the standard name.
const seed = (saved: SavedName): NameDraftState => ({ value: saved ?? "", seededFromSaved: saved !== undefined });

/** The Studio's unsaved name for Annie. Re-seeds on every open, and once more when the saved name first arrives. */
export const useAnnieNameDraft = ({ open, saved, defaultName }: { open: boolean; saved: SavedName; defaultName: string }) => {
	const [state, setState] = useState(() => seed(saved));
	const [wasOpen, setWasOpen] = useState(open);
	if (open !== wasOpen) {
		setWasOpen(open);
		if (open) setState(seed(saved));
	} else if (open && saved !== undefined && !state.seededFromSaved) {
		setState(seed(saved));
	}

	const setValue = useCallback((value: string) => {
		const codePoints = [...value.normalize("NFC")];
		const capped = codePoints.length > ANNIE_NAME_MAX_LENGTH ? codePoints.slice(0, ANNIE_NAME_MAX_LENGTH).join("") : value;
		setState((prev) => ({ ...prev, value: capped }));
	}, []);
	// DEFAULT: before settings load there is no saved name to keep; isDirty is false until they do.
	const toSave = toSavedAnnieName(state.value, defaultName, saved ?? null);
	return {
		value: state.value,
		setValue,
		toSave,
		/** What the preview calls her while the user types. */
		// DEFAULT: null = the standard name, so the preview shows it.
		displayName: toSave ?? defaultName,
		isValid: isValidAnnieName(toSave),
		isDirty: saved !== undefined && toSave !== saved,
	};
};

export type AnnieNameDraft = ReturnType<typeof useAnnieNameDraft>;
